import React, { memo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Flex } from 'onefaceui-react-components';

import { getResourceTo } from 'react/utils/utils';
import LocalzSwitchCheckbox from 'react/components/Integrations/SwitchCheckbox/LocalzSwitchCheckbox';
import LocalzIntegrationModal from 'react/containers/modals/LocalzIntegrationModal';
import { actionCreators } from 'react/api/actions/localzIntegration'

import IntegrationCard from './IntegrationCard';
import Loading from '../Loading';

function LocalzIntegrationCard({
  id,
  integrationData,
  active
}) {
  const dispatch = useDispatch();
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const tenant = Greenmile.Utils?.getTenant() ?? 'lts';

  const handleModalConnectIntegration = useCallback(() => {
    setIsIntegrationModalOpen((prevState) => !prevState);
  }, []);

  const closeModalAction = useCallback(() => {
    dispatch(actionCreators.clearIntegrationConnectState());

    setIsIntegrationModalOpen(false);
  }, []);

  const handleOnChangeSwitch = useCallback(({ checked }) => {
    if (checked) {
      handleModalConnectIntegration();
    }
  }, []);

  const confirmSwitchDisableCallback = useCallback(() => {
    const payload = {
      tenant
    };

    dispatch(actionCreators.disableIntegration(payload));
  }, [tenant]);

  const confirmModalAction = useCallback((data) => {
    if(integrationData) {
      const { name, id: idIntegration } = integrationData.integration;
      const { region, projectId, apikey, organizations } = data;

      const payload = {
        tenant,
        region,
        projectId,
        apikey,
        enabled: true,
        name,
        organizations,
        isConnected: !!idIntegration
      };

      dispatch(actionCreators.connectIntegration(payload));
    }
  }, [integrationData, tenant]);

  if(integrationData.isLoading) {
    return <Loading />;
  }

  const { integration } = integrationData;

  return (
    <IntegrationCard
      id={id}
      isConfigurable
      hasError={false}
      cardName={integration.name}
      active={active}
      onClickOnConfig={handleModalConnectIntegration}
    >
      <div className="IntegrationSlide">
        {isIntegrationModalOpen && (
          <LocalzIntegrationModal
            id={id}
            isOpen={isIntegrationModalOpen}
            i18n={Monet.I18n}
            title={getResourceTo('IntegrationModal.ConnectIntegration.Title', integration.name)}
            message={getResourceTo('IntegrationModal.ConnectIntegration.Message', integration.name)}
            integrationData={JSON.stringify(integrationData)}
            confirmActionCallback={confirmModalAction}
            cancelActionCallback={closeModalAction}
          />
        )}
        <Flex flexDirection="column">
          <LocalzSwitchCheckbox
            checked={active}
            onChange={handleOnChangeSwitch}
            onDisableCallback={confirmSwitchDisableCallback}
            integrationData={JSON.stringify(integrationData)}
          />
          <div data-testid={`integration-description-${id}`} className="IntegrationDescription">
            <span>
              {getResourceTo('localzIntegration.card.description')}
            </span>
          </div>
          <div data-testid={`view-logs-${id}`} className="ViewLogs">
            <Link data-testid={`view-logs-link-${id}`} to="/Integrations/localz/logs">
              <span>{getResourceTo('IntegrationCard.ViewLogsButton')}</span>
            </Link>
          </div>
        </Flex>
      </div>
    </IntegrationCard>
  );
}

LocalzIntegrationCard.propTypes = {
  id: PropTypes.string,
  integrationData: PropTypes.object.isRequired,
  active: PropTypes.bool.isRequired,
};

export default memo(LocalzIntegrationCard);
